import { z } from 'zod';
import { orgAddressSchema } from './client-profile.js';
import { isoCountryCodeSchema } from './customer-org-jurisdiction.js';

/**
 * SIL-993 — individual_profile (861) is the authoritative identity + preference
 * record for an individual customer_org. Buying and selling unlock TOGETHER,
 * in one step, when the required personal fields below are complete — there is
 * no separate buyer-complete / seller-complete state for an individual, unlike
 * the business seller_org/buyer_org dual-profile split (client-profile.ts).
 *
 * SIL-1011: an individual is a home-lab hobbyist, not a data-center buyer —
 * this schema is its OWN strict, consumer-only vocabulary. It does NOT reuse
 * orgPreferenceFieldsSchema (client-profile.ts): no DC capability, no ITAD
 * services (data wipe/asset tagging/install/decommission), no
 * asset-class/manufacturer/region matching prefs, and shipping is limited to
 * the 3 methods a home-lab shipment can plausibly use — no palletized/white
 * glove. Keeping this a distinct schema means the business org's vocabulary
 * (client-profile.ts) is untouched by this change.
 */
export const CONSUMER_SHIPPING_METHOD_SLUGS = ['parcel', 'freight', 'international'] as const;
export type ConsumerShippingMethodSlug = (typeof CONSUMER_SHIPPING_METHOD_SLUGS)[number];

/**
 * `.strict()` — an individual PATCH carrying a removed key (prefDcCapability,
 * prefServices, prefAssetClasses/prefManufacturers/prefRegions) or a removed
 * shipping value (palletized/white_glove) 400s instead of being silently
 * dropped or silently stored. Use `readIndividualPreferences` below, not this
 * schema, to parse a STORED jsonb value — old rows may still hold the
 * removed keys (pre-SIL-1011) and must not fail to load.
 */
export const individualPreferencesSchema = z
  .object({
    prefShippingMethods: z.array(z.enum(CONSUMER_SHIPPING_METHOD_SLUGS)).optional(),
  })
  .strict();
export type IndividualPreferences = z.infer<typeof individualPreferencesSchema>;

/**
 * Lenient read-side parse for a STORED buyingPreferences/sellingPreferences
 * jsonb value. Never throws: an old row may still carry the pre-SIL-1011
 * business vocabulary (prefDcCapability, prefServices, palletized/white_glove
 * shipping values, etc) — those keys/values are silently dropped rather than
 * failing the read. Use this (not individualPreferencesSchema.parse) for
 * every read path; use individualPreferencesSchema for writes.
 */
export function readIndividualPreferences(raw: unknown): IndividualPreferences | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const shippingRaw = (raw as Record<string, unknown>)['prefShippingMethods'];
  if (!Array.isArray(shippingRaw)) return {};
  const prefShippingMethods = shippingRaw.filter((v): v is ConsumerShippingMethodSlug =>
    (CONSUMER_SHIPPING_METHOD_SLUGS as readonly string[]).includes(v as string),
  );
  return prefShippingMethods.length > 0 ? { prefShippingMethods } : {};
}

/**
 * GET /api/client/individual-profile response shape.
 *
 * countryOfResidence (SIL-1011) is NOT an individual_profile column — it is
 * the org's customer_org_jurisdiction.country_code (758), sourced from the
 * SAME table the ops Jurisdiction tab reads/corrects. It is declared by the
 * user directly (a country-of-residence field, not derived from `address`
 * jsonb — migration 758's compliance rule: never parse free-form address
 * text for a jurisdiction determination). null until the user sets it.
 *
 * phone (SIL-1198): stored as E.164 going forward — the API's PATCH route
 * normalizes on write via `normalizePhoneToE164` (phone.ts). No backfill:
 * an old row may still hold a pre-SIL-1198 free-text value, so this read
 * shape stays a loose nullable string rather than an E.164-shaped one —
 * the customer portal's `formatPhoneForDisplay` (phone.ts) handles either.
 */
export const individualProfileSchema = z.object({
  customerOrgId: z.string().uuid(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string(),
  phone: z.string().nullable(),
  address: orgAddressSchema,
  countryOfResidence: isoCountryCodeSchema.nullable(),
  buyingPreferences: individualPreferencesSchema.nullable(),
  sellingPreferences: individualPreferencesSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type IndividualProfile = z.infer<typeof individualProfileSchema>;

/**
 * PATCH /api/client/individual-profile body. `email` is NOT editable here —
 * it is the invited email, independent of the Cognito user record (861's
 * comment on individual_profile.email); changing it is out of scope.
 * `.strict()` so a stray/forbidden field 400s instead of being silently
 * dropped.
 *
 * countryOfResidence, when supplied, writes customer_org_jurisdiction
 * directly (same upsert the seller-onboarding /complete route uses) — it is
 * never derived from the `address` field on this same request.
 *
 * SIL-1190: countryOfResidence is intentionally a loose `z.string()` here,
 * NOT `isoCountryCodeSchema` — the route validates its ISO-2 format itself,
 * per-field, so a malformed value 400s only that field and everything else
 * in the same PATCH still persists (see individualProfileUpdateSchema's
 * former all-or-nothing failure mode: a non-ISO value like "USA" used to
 * 400 the entire request, silently dropping firstName/lastName/phone/address
 * the user had also just entered). The customer portal's ISO-code select
 * (individual-profile.tsx) keeps normal use from ever sending an invalid
 * value in the first place; this is defense-in-depth for any other caller.
 *
 * phone (SIL-1198) is the same "loose string here, per-field validation in
 * the route" pattern as countryOfResidence, for the same reason: the route
 * normalizes it to E.164 via `normalizePhoneToE164` and reports an invalid
 * number as `fieldErrors.phone` rather than 400ing the whole PATCH. The
 * customer portal's dial-code selector + live formatting keeps normal use
 * from ever sending anything but a valid E.164 value.
 */
export const individualProfileUpdateSchema = z
  .object({
    firstName: z.string().min(1).nullable().optional(),
    lastName: z.string().min(1).nullable().optional(),
    phone: z.string().min(1).nullable().optional(),
    address: orgAddressSchema,
    countryOfResidence: z.string().min(1).optional(),
    buyingPreferences: individualPreferencesSchema.nullable().optional(),
    sellingPreferences: individualPreferencesSchema.nullable().optional(),
  })
  .strict();
export type IndividualProfileUpdate = z.infer<typeof individualProfileUpdateSchema>;

/**
 * RUF-534-style shared "is this individual profile complete enough to
 * transact" predicate. Required: firstName, lastName, email, phone, an
 * address with ALL FIVE fields non-empty — street, city, state, postal,
 * country (SIL-1190: unlike profileCompleteness() in client-profile.ts,
 * which stays street/city/country-only for a business org — this is a
 * deliberate divergence, not a mirror), and (SIL-1011) countryOfResidence —
 * the user's declared customer_org_jurisdiction.country_code, NOT derived
 * from address. buyingPreferences/sellingPreferences are NOT required —
 * they are preference hatches, not identity fields.
 *
 * Column list every call site (clientAuth, GET /me, GET /individual-profile)
 * must select so they all evaluate the predicate against identical inputs.
 */
export const INDIVIDUAL_PROFILE_COMPLETENESS_COLUMNS = [
  'firstName',
  'lastName',
  'email',
  'phone',
  'address',
  'countryOfResidence',
] as const;

export type IndividualProfileCompletenessRow = {
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  email: string | null | undefined;
  phone: string | null | undefined;
  address: unknown;
  countryOfResidence: string | null | undefined;
};

export type IndividualProfileMissingField =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'address'
  | 'countryOfResidence';

export interface IndividualProfileCompletenessResult {
  complete: boolean;
  missing: IndividualProfileMissingField[];
}

/** True for a string with non-whitespace content; false for null/undefined/blank. */
function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

export function individualProfileCompleteness(
  row: IndividualProfileCompletenessRow,
): IndividualProfileCompletenessResult {
  const missing: IndividualProfileMissingField[] = [];

  if (!isNonEmptyString(row.firstName)) missing.push('firstName');
  if (!isNonEmptyString(row.lastName)) missing.push('lastName');
  if (!isNonEmptyString(row.email)) missing.push('email');
  if (!isNonEmptyString(row.phone)) missing.push('phone');

  const address = row.address;
  const isAddressObject = typeof address === 'object' && address !== null && !Array.isArray(address);
  const addressRecord = isAddressObject ? (address as Record<string, unknown>) : {};
  // SIL-1190: all five address fields are required for an individual — no
  // optional subset, unlike the business profileCompleteness() rule.
  const hasCompleteAddress =
    isNonEmptyString(addressRecord['street']) &&
    isNonEmptyString(addressRecord['city']) &&
    isNonEmptyString(addressRecord['state']) &&
    isNonEmptyString(addressRecord['postal']) &&
    isNonEmptyString(addressRecord['country']);
  if (!hasCompleteAddress) missing.push('address');

  if (!isNonEmptyString(row.countryOfResidence)) missing.push('countryOfResidence');

  return { complete: missing.length === 0, missing };
}
